import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Pagination,
} from '@mui/material';
import {
  Search as SearchIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
  Login as LoginIcon,
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Storage as StorageIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { LoadingSpinner } from '../components/LoadingSpinner';

interface AuditLog {
  id: string;
  actor_email: string;
  action: string;
  connection_id?: string;
  table_name?: string;
  rows_affected?: number;
  sql_query?: string;
  query_params?: any;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
}

export const Audit: React.FC = () => {
  const { user, apiCall } = useAuth();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // 通知状态
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  const pageSize = 20;

  // 检查管理员权限
  const isAdmin = user?.role.name === 'Administrator';

  // 加载审计日志
  const loadAuditLogs = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: pageSize.toString(),
        ...(actionFilter && { action: actionFilter }),
        ...(searchTerm && { search: searchTerm })
      });
      
      const data = await apiCall(`/api/audit?${queryParams}`);
      setLogs(data.logs || []);
      setTotal(data.pagination?.total || 0);
      setTotalPages(data.pagination?.totalPages || 0);
    } catch (error) {
      console.error('加载审计日志失败:', error);
      showSnackbar('加载审计日志失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 显示通知
  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  // 获取操作图标
  const getActionIcon = (action: string) => {
    switch (action) {
      case 'LOGIN':
        return <LoginIcon fontSize="small" />;
      case 'CREATE_USER':
        return <PersonAddIcon fontSize="small" />;
      case 'UPDATE_USER':
        return <EditIcon fontSize="small" />;
      case 'DELETE_USER':
        return <DeleteIcon fontSize="small" />;
      case 'CREATE_CONNECTION':
        return <StorageIcon fontSize="small" />;
      case 'TEST_CONNECTION':
        return <StorageIcon fontSize="small" />;
      default:
        return <HistoryIcon fontSize="small" />;
    }
  };

  // 获取操作颜色
  const getActionColor = (action: string) => {
    switch (action) {
      case 'LOGIN':
        return 'info';
      case 'CREATE_USER':
      case 'CREATE_CONNECTION':
        return 'success';
      case 'UPDATE_USER':
        return 'warning';
      case 'DELETE_USER':
        return 'error';
      default:
        return 'default';
    }
  };

  // 操作中文映射
  const actionMap: Record<string, string> = {
    'LOGIN': '登录',
    'LOGOUT': '登出',
    'CREATE_USER': '创建用户',
    'UPDATE_USER': '更新用户',
    'DELETE_USER': '删除用户',
    'CREATE_CONNECTION': '创建连接',
    'UPDATE_CONNECTION': '更新连接',
    'DELETE_CONNECTION': '删除连接',
    'TEST_CONNECTION': '测试连接',
    'EXECUTE_QUERY': '执行查询',
    'EXPORT_DATA': '导出数据',
    'CHANGE_PASSWORD': '修改密码',
  };

  // 格式化日期
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('zh-CN');
  };

  useEffect(() => {
    if (!isAdmin) {
      showSnackbar('权限不足：只有管理员可以查看审计日志', 'error');
      return;
    }
    loadAuditLogs();
  }, [isAdmin, page, actionFilter, searchTerm]);

  if (!isAdmin) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          审计日志
        </Typography>
        <Alert severity="error">
          权限不足：只有管理员可以查看审计日志。
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return <LoadingSpinner message="加载审计日志..." />;
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        审计日志
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        系统操作记录，用于安全审计和问题排查（共 {total} 条记录）
      </Typography>

      {/* 筛选控件 */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label="搜索"
            placeholder="搜索用户邮箱或IP地址..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 300 }}
          />

          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>操作类型</InputLabel>
            <Select
              value={actionFilter}
              label="操作类型"
              onChange={(e) => setActionFilter(e.target.value)}
            >
              <MenuItem value="">全部</MenuItem>
              {Object.entries(actionMap).map(([key, value]) => (
                <MenuItem key={key} value={key}>{value}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* 审计日志表格 */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>时间</TableCell>
              <TableCell>用户</TableCell>
              <TableCell>操作</TableCell>
              <TableCell>表名</TableCell>
              <TableCell>影响行数</TableCell>
              <TableCell>IP地址</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.map((log) => (
              <TableRow key={log.id}>
                <TableCell>
                  <Typography variant="body2">
                    {formatDate(log.created_at)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon fontSize="small" />
                    <Typography variant="body2">
                      {log.actor_email || '系统'}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    icon={getActionIcon(log.action)}
                    label={actionMap[log.action] || log.action}
                    color={getActionColor(log.action) as any}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {log.table_name || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {log.rows_affected || 0}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {log.ip_address || '-'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
            {logs.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <SecurityIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                      暂无审计日志
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      系统操作记录将显示在这里
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 分页 */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, newPage) => setPage(newPage)}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* 通知 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}; 